import { useState } from "react";

/**
 * Public brand-signup form mounted at #/brands/signup inside the app shell.
 * No SDK / auth required — anyone can request access. Posts to the public
 * POST /api/brand/signup, which inserts a pending storesync.brand_signup row.
 */

type FieldErrors = Partial<Record<"name" | "email" | "brand_name" | "website" | "notes", string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function looksLikeUrl(raw: string): boolean {
  const v = raw.trim();
  if (!v) return false;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function BrandSignupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brandName, setBrandName] = useState("");
  const [website, setWebsite] = useState("");
  const [wantsApi, setWantsApi] = useState(false);
  const [notes, setNotes] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const validate = (): FieldErrors => {
    const errs: FieldErrors = {};
    if (name.trim().length < 2) errs.name = "Please enter your name.";
    if (!EMAIL_RE.test(email.trim())) errs.email = "Enter a valid email address.";
    if (brandName.trim().length < 1) errs.brand_name = "Brand name is required.";
    if (!looksLikeUrl(website)) errs.website = "Enter a valid website URL.";
    if (notes.length > 1000) errs.notes = "Notes must be 1000 characters or fewer.";
    return errs;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const res = await fetch("/api/brand/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          brand_name: brandName,
          website,
          wants_api_access: wantsApi,
          notes,
          _honeypot_company: honeypot,
        }),
      });
      if (res.status === 201) {
        setSubmittedEmail(email.trim());
        return;
      }
      if (res.status === 429) {
        setFormError("Too many requests. Try again in an hour.");
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string; fields?: FieldErrors };
      if (body.fields) setFieldErrors(body.fields);
      setFormError("Please check your entries.");
    } catch {
      setFormError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedEmail) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Thanks!</h2>
          <p className="text-sm text-gray-600">
            We'll review your request and email you at{" "}
            <span className="font-medium text-gray-800">{submittedEmail}</span>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Request StoreSync access</h2>
        <p className="text-sm text-gray-600 mt-1 max-w-xl">
          Tell us about your brand and we'll review your request to match your retailer data to the
          California graph.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <form className="space-y-5" onSubmit={submit} noValidate>
          <Field label="Your name" error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Work email" error={fieldErrors.email}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Brand name" error={fieldErrors.brand_name}>
            <input
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              maxLength={100}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Brand website" error={fieldErrors.website}>
            <input
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="yourbrand.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </Field>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={wantsApi}
              onChange={(e) => setWantsApi(e.target.checked)}
              className="h-4 w-4"
            />
            I'd like API access
          </label>

          <Field label="Notes / anything else? (optional)" error={fieldErrors.notes}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </Field>

          {/* Honeypot: hidden from users; bots that fill it get a fake success. */}
          <input
            type="text"
            name="_honeypot_company"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
          />

          {formError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? "Submitting…" : "Request access"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
